/**
 * Entry point for the React app: mounts <App /> wrapped in the global
 * providers (React Query, Router, Theme, Auth) + the Sonner toaster.
 * Included from src/index.html.
 */
import { StrictMode, useEffect, useState, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";

import { App } from "./App";
import { queryClient } from "./lib/queryClient";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import {
  initEngagementTracker,
  teardownEngagementTracker,
  initDiagnosticShortcuts,
  teardownDiagnosticShortcuts,
  emitSessionFingerprint,
} from "./lib/easterEggs";
import { SystemDiagOverlay } from "./components/ui/SystemDiagOverlay";
import "./index.css";

// Emit session fingerprint (console credits) on initial page load.
emitSessionFingerprint();

/**
 * InteractionTelemetryProvider — initializes engagement tracking
 * and diagnostic shortcut listeners on mount. Renders the
 * SystemDiagOverlay when triggered by valid input sequences.
 */
function InteractionTelemetryProvider({ children }: { children: React.ReactNode }) {
  const [diagOpen, setDiagOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastWho, setToastWho] = useState<"jcfp" | "sad" | null>(null);

  const handleSequenceMatch = useCallback(() => {
    setDiagOpen(true);
  }, []);

  const handleDiagShortcut = useCallback((who: "jcfp" | "sad") => {
    setToastWho(who);
    setToastOpen(true);
    setTimeout(() => setToastOpen(false), 4000);
  }, []);

  useEffect(() => {
    initEngagementTracker(handleSequenceMatch);
    initDiagnosticShortcuts(handleDiagShortcut);
    return () => {
      teardownEngagementTracker();
      teardownDiagnosticShortcuts();
    };
  }, [handleSequenceMatch, handleDiagShortcut]);

  return (
    <>
      {children}
      <SystemDiagOverlay
        open={diagOpen}
        onClose={() => setDiagOpen(false)}
        variant="full"
      />
      <SystemDiagOverlay
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        variant="toast"
        who={toastWho}
      />
    </>
  );
}

const elem = document.getElementById("root")!;

const app = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <InteractionTelemetryProvider>
              <App />
              <Toaster
                position="top-right"
                theme="dark"
                richColors
                toastOptions={{
                  style: {
                    background: "rgba(20,20,40,0.85)",
                    backdropFilter: "blur(24px)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#fff",
                  },
                }}
              />
            </InteractionTelemetryProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);

// Vite's @vitejs/plugin-react provides React Fast Refresh automatically,
// so a plain mount is all that's needed (no manual HMR root reuse).
createRoot(elem).render(app);
